<section class="bg-gray-50 min-h-screen py-8 antialiased md:py-16">
  <div class="mx-auto max-w-2xl px-4 2xl:px-0">
    <!-- Skeleton loader -->
    <nz-skeleton *ngIf="loading" [nzActive]="true" [nzParagraph]="{ rows: 10 }"></nz-skeleton>

    <div *ngIf="!loading && rsvp">
      <!-- RSVP status card -->
      <nz-card [nzBordered]="true" class="shadow-sm rounded-lg overflow-hidden">
        <div class="text-center p-6 bg-white border-b border-gray-100">
          <h1 nz-typography class="text-2xl font-bold mb-2">RSVP For {{ rsvp.eventName }}</h1>
          <p class="text-gray-500 mb-4">{{ rsvp.eventDate | date: 'fullDate' }}</p>

          <div class="flex justify-center mb-6">
            <nz-tag [nzColor]="getStatusColor(rsvp.status)" class="px-4 py-1 text-base font-medium rounded-full">
              {{ rsvp.status }}
            </nz-tag>
          </div>
        </div>

        <div class="p-1">
          <!-- Pending state - Countdown and actions -->
          <div *ngIf="rsvp.status === 'PENDING'">
            <div class="bg-gray-50 rounded-xl p-8 mb-8 text-center border border-gray-100">
              <h3 class="text-sm font-semibold uppercase tracking-wider text-gray-500 mb-2">RSVP Timer</h3>
              <p class="text-4xl font-bold font-mono tracking-tight text-gray-900">{{ timeRemaining }}</p>
              <p class="text-sm text-gray-400 mt-2">Time remaining to confirm or cancel</p>
            </div>

            <nz-descriptions [nzTitle]="'RSVP Details'" nzLayout="vertical" [nzColumn]="{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }">
              <nz-descriptions-item nzTitle="Customer Name">{{ rsvp.customerName }}</nz-descriptions-item>
              <nz-descriptions-item nzTitle="Email address">{{ rsvp.customerEmail }}</nz-descriptions-item>
              <nz-descriptions-item nzTitle="Event Date">{{ rsvp.eventDate | date: 'medium' }}</nz-descriptions-item>
              <nz-descriptions-item nzTitle="Expires At">{{ rsvp.expiresAt | date: 'medium' }}</nz-descriptions-item>
            </nz-descriptions>

            <nz-divider></nz-divider>

            <div class="flex flex-col sm:flex-row gap-4 mt-8 justify-center">
              <button
                nz-button
                nzType="primary"
                nzSize="large"
                class="min-w-[160px] h-12 text-base font-semibold shadow-md bg-gray-900 border-none hover:bg-gray-800"
                (click)="confirmAttendance()"
                [nzLoading]="confirming"
                [disabled]="cancelling"
              >
                <span nz-icon nzType="check-circle" nzTheme="outline"></span>
                Confirm Attendance
              </button>
              <button
                nz-button
                nzType="default"
                nzSize="large"
                class="min-w-[160px] h-12 text-base font-semibold"
                (click)="cancelAttendance()"
                [nzLoading]="cancelling"
                [disabled]="confirming"
              >
                <span nz-icon nzType="close-circle" nzTheme="outline"></span>
                Cancel
              </button>
            </div>
          </div>

          <!-- Confirmed state -->
          <div *ngIf="rsvp.status === 'CONFIRMED'">
            <nz-result
              nzStatus="success"
              nzTitle="Attendance Confirmed!"
              nzSubTitle="Your RSVP for {{ rsvp.eventName }} has been successfully confirmed. Your ticket has been sent to {{ rsvp.customerEmail }}. Please check your inbox (and spam folder) for further details."
            >
              <div nz-result-content>
                <div class="bg-gray-50 p-6 rounded-lg border border-success-light">
                  <p class="text-sm font-semibold mb-4 text-center">Confirmation Details</p>
                  <nz-descriptions [nzColumn]="1" nzSize="small">
                    <nz-descriptions-item nzTitle="Confirmed At">{{ rsvp.confirmedAt | date: 'medium' }}</nz-descriptions-item>
                    <nz-descriptions-item nzTitle="Name">{{ rsvp.customerName }}</nz-descriptions-item>
                    <nz-descriptions-item nzTitle="Event">{{ rsvp.eventName }}</nz-descriptions-item>
                  </nz-descriptions>
                </div>
              </div>
              <div nz-result-extra>
                <button nz-button nzType="primary" (click)="returnToHome()" class="h-10 bg-gray-900 border-none">Return to Homepage</button>
              </div>
            </nz-result>
          </div>

          <!-- Cancelled state -->
          <div *ngIf="rsvp.status === 'CANCELLED'">
            <nz-result
              nzStatus="info"
              nzTitle="RSVP Cancelled"
              nzSubTitle="You have cancelled your attendance for this event. The reserved spot has been released and is now available for other customers."
            >
              <div nz-result-extra>
                <button nz-button nzType="primary" (click)="returnToHome()" class="h-10 bg-gray-900 border-none">Browse Other Events</button>
              </div>
            </nz-result>
          </div>

          <!-- Expired state -->
          <div *ngIf="rsvp.status === 'EXPIRED' || isExpired">
            <nz-result
              nzStatus="warning"
              nzTitle="RSVP Expired"
              nzSubTitle="We're sorry, this RSVP link has expired. RSVP links must be confirmed within a specific timeframe as set by the event organizer."
            >
              <div nz-result-content>
                <nz-alert
                  nzType="warning"
                  nzMessage="Expired Window"
                  nzDescription="The deadline to RSVP for this event passed on {{ rsvp.expiresAt | date: 'medium' }}."
                  nzShowIcon
                ></nz-alert>
              </div>
              <div nz-result-extra>
                <button nz-button nzType="primary" (click)="returnToHome()" class="h-10 bg-gray-900 border-none">Return to Homepage</button>
              </div>
            </nz-result>
          </div>
        </div>
      </nz-card>
    </div>

    <!-- Error/No RSVP state -->
    <div *ngIf="!loading && !rsvp" class="mt-8">
      <nz-result
        nzStatus="error"
        nzTitle="RSVP Not Found"
        nzSubTitle="The RSVP token provided is invalid or has already been used. Please check the link in your email and try again."
      >
        <div nz-result-extra>
          <button nz-button nzType="primary" (click)="returnToHome()" class="bg-gray-900 border-none h-10">Return Home</button>
        </div>
      </nz-result>
    </div>
  </div>
</section>
